import type {
  AnsweredQuestion,
  AssessmentResponse,
  AssessmentScoreLabel,
  AssessmentScoreTrend,
  ItemScoreHistoryEntry,
  SubstitutionAvailability,
} from '@evaltick/shared';
import { BaseAPIClient } from './base';
import type { InterviewTemplate } from './interviewTemplates';

// =============================================================================
// TYPES
// =============================================================================

/** Mirrors the AssessmentStatus enum in the Prisma schema (server-only). */
export const AssessmentStatus = {
  InProgress: 'IN_PROGRESS',
  Completed: 'COMPLETED',
} as const;
export type AssessmentStatus = (typeof AssessmentStatus)[keyof typeof AssessmentStatus];

export type { AnsweredQuestion };

export interface Assessment {
  id: string;
  userId: number;
  interviewTemplateId: string;
  status: AssessmentStatus;
  totalScore: number | null;
  createdAt: string;
  completedAt: string | null;
  interviewTemplate: InterviewTemplate;
  responses: AssessmentResponse[];
  /** Which questions this sitting has already been answered on. Only the create and fetch-by-id endpoints compute it. */
  answeredQuestions?: AnsweredQuestion[];
  /** Empty = unscoped (fresh/full sitting). Populated = the item ids this sitting is
   * scoped to — read it back rather than assuming it matches the ids you sent. */
  retakeItemIds: string[];
  /** AI-generated session write-up — null until generated, in the background, shortly after completion. */
  summaryNarrative: string | null;
  /**
   * The preceding sitting's own `totalScore` — null when this is the first.
   * Optional because only the fetch-by-id endpoint computes it; create/complete
   * return the assessment row as-is.
   */
  previousTotalScore?: number | null;
  /**
   * `totalScore - previousTotalScore` — the movement in the overall score
   * between the two sittings, so it always reconciles with the score ring.
   * Null until this sitting is completed and has a totalScore of its own.
   */
  totalScoreDelta?: number | null;
  /**
   * One entry per template question the candidate has ever answered, carrying
   * their latest attempt at it — covers questions this sitting skipped, which
   * is what lets the retake picker rank the whole template. Optional on the
   * same terms as the two fields above: only the fetch-by-id endpoint sends it.
   */
  itemScoreHistory?: ItemScoreHistoryEntry[];
  /**
   * Per template item id, what a "different question" retry would land on
   * right now — lets the retake picker warn the bank has nothing new left
   * before the sitting is created. Optional like the fields above: only
   * fetch-by-id sends it.
   */
  substitutionAvailability?: Record<string, SubstitutionAvailability>;
  /**
   * Score-trend chart data for the results page — up to 10 sittings of this
   * template (this one plus prior ones), null when there's no trend yet (the
   * candidate's first sitting). Optional on the same terms as the fields
   * above: only the fetch-by-id endpoint sends it.
   */
  scoreTrend?: AssessmentScoreTrend | null;
  /**
   * True for every completed access-code guest sitting — a guest never sees
   * their results. Every score/response/summary field is server-withheld while
   * true, not just UI-hidden. Optional like the fields above: only fetch-by-id/complete send it.
   */
  resultsPending?: boolean;
  /**
   * Items where "different question" was requested but the bank had nothing
   * else, so the last-asked question was kept instead. Only meaningful on the
   * response that actually resolved this sitting's substitutions; empty
   * otherwise. Only the create endpoint sends it.
   */
  substitutionDeadEnds?: string[];
}

/** One of the current user's own assessments — for the session-history list. */
export interface AssessmentListItemForUser {
  id: string;
  status: AssessmentStatus;
  totalScore: number | null;
  label: AssessmentScoreLabel | null;
  createdAt: string;
  completedAt: string | null;
  interviewTemplate: { id: string; name: string; revokedAt: string | null; questionCount: number };
  /** See Assessment.resultsPending — totalScore/label stay null while this is true. */
  resultsPending: boolean;
}

export interface PaginatedAssessmentsForUser {
  data: AssessmentListItemForUser[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// =============================================================================
// SERVICE
// =============================================================================

/** What starting a sitting returns: the sitting plus `answeredQuestions` if it's a resumed one. No scored `responses`. */
export type StartedAssessment = Omit<Assessment, 'responses'>;

/** The fields resuming a sitting reads, from either the start or fetch-by-id payload. */
export type ResumableAssessment = Pick<
  Assessment,
  'id' | 'interviewTemplate' | 'retakeItemIds' | 'answeredQuestions'
>;

export class AssessmentService extends BaseAPIClient {
  async createAssessment(
    interviewTemplateId: string,
    itemIds?: string[],
    differentItemIds?: string[],
    retakeFromAssessmentId?: string
  ): Promise<StartedAssessment> {
    return this.post<StartedAssessment>('/assessments', {
      interviewTemplateId,
      itemIds,
      differentItemIds,
      retakeFromAssessmentId,
    });
  }

  async getAssessment(id: string): Promise<Assessment> {
    return this.get<Assessment>(`/assessments/${id}`);
  }

  /**
   * The id of the user's open sitting for this template, or null. Creates
   * nothing and charges nothing — for telling a resume from a fresh start
   * before committing to either, since `createAssessment` silently resumes.
   */
  async findActiveAssessmentId(interviewTemplateId: string): Promise<string | null> {
    const { assessmentId } = await this.get<{ assessmentId: string | null }>(
      `/assessments/active?interviewTemplateId=${encodeURIComponent(interviewTemplateId)}`
    );
    return assessmentId;
  }

  async completeAssessment(id: string): Promise<Assessment> {
    return this.patch<Assessment>(`/assessments/${id}/complete`);
  }

  /** The current user's own assessments — for the session-history list. */
  async listMine(page: number, limit: number): Promise<PaginatedAssessmentsForUser> {
    return this.get<PaginatedAssessmentsForUser>(`/assessments?page=${page}&limit=${limit}`);
  }
}

export default AssessmentService;
